import { Button } from "@/components/ui/button";
import { Calendar, Menu, X, Zap, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id: string) => {
    setIsOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: "How It Works", id: "features" },
    { label: "What's Included", id: "whats-included" },
    { label: "FAQ", id: "faq" },
  ];

  return (
    <>
      {/* Mobile menu backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <header className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
        {/* Hidden staff login icon - top right corner */}
        <Link 
          to="/dashboard" 
          className="absolute top-6 right-6 p-2 opacity-0 hover:opacity-100 transition-opacity duration-300 text-muted-foreground hover:text-primary"
          aria-label="Staff login"
        >
          <Lock className="w-4 h-4" />
        </Link>
        
        <nav 
          className={`
            max-w-4xl mx-auto px-4 md:px-6 py-3
            rounded-2xl border border-border/50
            backdrop-blur-xl bg-card/90
            shadow-lg shadow-background/20
            transition-all duration-300
            ${isScrolled ? 'bg-card/95 border-primary/20' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 group">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-base md:text-lg text-foreground">
                Lead<span className="text-primary">Lancer</span>
              </span>
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavClick(link.id)}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-secondary/50"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:block">
              <Button 
                size="sm"
                className="px-5 bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => handleNavClick('booking')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book a Call
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-border/50">
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleNavClick(link.id)}
                    className="px-4 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary/50 text-left"
                  >
                    {link.label}
                  </button>
                ))}
                <Button 
                  className="mt-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={() => handleNavClick('booking')}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Call
                </Button>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
};

export default Navigation;
